
import { Component, DestroyRef, OnInit, computed, inject, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  EmptyComponent,
  SelectComponent,
  TableComponent,
  type ActionEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import { catchError, finalize, map, of, switchMap, timeout } from 'rxjs';
import { IngestProjectService } from '../../../../../data-access/src/lib/ingest/ingest-project.service';
import { IngestProject, IngestRemoteFile } from '../../../../../domain/src/lib/ingest/ingest-project.model';
import { FolderTreeComponent } from '../folder-tree/folder-tree.component';
import { buildFolderTreeFromSubfolders, FolderTreeNode } from '../ingest-folder-utils';
import { INGEST_MODULE_ENV, readVaultItemsPerPageFromPreferences, VAULT_TABLE_PAGE_SIZE_OPTIONS } from '@nebula/shared-vault/data-access';
import { getMockRemoteFiles, getMockSubfolders, resolvePreviewProject } from '../ingest-ui-mock.data';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../../vault-table-pagination-bind.util';

/** Fila de tabla con etiquetas ya formateadas para `lib-table`. */
interface IngestFileRow extends IngestRemoteFile {
  sizeLabel: string;
  typeLabel: string;
}

@Component({
  selector: 'app-ingest-files',
  standalone: true,
  imports: [
    ButtonComponent,
    SelectComponent,
    TableComponent,
    EmptyComponent,
    FolderTreeComponent,
    FieldLabelComponent,
    PageHeaderComponent,
    GridFormComponent,
    GridFormItemComponent,
    VaultPageAlertComponent,
],
  templateUrl: './ingest-files.component.html',
  styleUrls: ['./ingest-files.component.scss'],
})
export class IngestFilesComponent implements OnInit {
  private readonly ingestProjectService = inject(IngestProjectService);
  private readonly ingestEnv = inject(INGEST_MODULE_ENV);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly projects = signal<IngestProject[]>([]);
  readonly loadingProjects = signal(false);
  readonly project = signal<IngestProject | null>(null);
  readonly loadingProject = signal(false);
  readonly treeRoot = signal<FolderTreeNode>({
    label: 'Raíz',
    path: '',
    children: [],
  });
  readonly selectedPath = signal('');
  readonly fileRows = signal<IngestFileRow[]>([]);
  readonly loadingFiles = signal(false);
  readonly error = signal<string | null>(null);

  readonly projectOptions = computed(() =>
    this.projects().map((p) => ({ label: `${p.name} (${p.folder_path})`, value: p.id })),
  );

  selectedProjectId = model<number | undefined>(undefined);

  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  readonly filesTableConfig = signal<TableConfig<IngestFileRow>>({
    columns: [
      { key: 'name', label: 'Nombre', sortable: true },
      { key: 'sizeLabel', label: 'Tamaño', sortable: true, align: 'right' },
      { key: 'typeLabel', label: 'Tipo', sortable: true },
    ],
    actions: [
      {
        label: 'Abrir',
        icon: 'FolderOpen',
        action: 'open',
        visible: (row) => row.is_folder,
      },
    ],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'open',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
    initialSort: { column: 'name', direction: 'asc' },
  });

  constructor() {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
  }

  ngOnInit(): void {
    this.loadProjectList();
    this.route.paramMap
      .pipe(
        map((p) => p.get('projectId')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((raw) => {
        const id = raw ? Number(raw) : NaN;
        if (raw && Number.isFinite(id) && id > 0) {
          this.selectedProjectId.set(id);
          this.loadProjectContext(id);
        } else {
          this.selectedProjectId.set(undefined);
          this.project.set(null);
          this.resetTreeAndFiles();
        }
      });
  }

  private loadProjectList(): void {
    this.loadingProjects.set(true);
    if (this.ingestEnv.previewMode) {
      const previewProjects = [1, 2, 3].reduce<IngestProject[]>(
        (acc, index) => {
          const project = resolvePreviewProject(index);
          if (project) {
            acc.push(project);
          }
          return acc;
        },
        [],
      );
      this.projects.set(previewProjects);
      this.loadingProjects.set(false);
      return;
    }
    this.ingestProjectService
      .list()
      .pipe(
        timeout(30000),
        finalize(() => this.loadingProjects.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (list) => {
          this.projects.set(list ?? []);
        },
        error: () => {
          this.projects.set([]);
          this.error.set('No se pudieron cargar los expedientes.');
        },
      });
  }

  onProjectPicked(id: number | undefined): void {
    this.selectedProjectId.set(id);
    if (!id) {
      void this.router.navigate(['files'], { relativeTo: this.route.parent });
      return;
    }
    void this.router.navigate(['files', id], { relativeTo: this.route.parent });
  }

  private loadProjectContext(projectId: number): void {
    this.loadingProject.set(true);
    this.error.set(null);
    if (this.ingestEnv.previewMode) {
      const p = resolvePreviewProject(projectId);
      if (!p) {
        this.error.set('Expediente no encontrado.');
        this.loadingProject.set(false);
        return;
      }
      this.project.set(p);
      const subs = getMockSubfolders(projectId);
      this.treeRoot.set(buildFolderTreeFromSubfolders(subs));
      this.selectedPath.set('');
      this.loadFilesForPath('');
      this.loadingProject.set(false);
      return;
    }

    this.ingestProjectService
      .getById(projectId)
      .pipe(
        timeout(45000),
        switchMap((p) => {
          this.project.set(p);
          return this.ingestProjectService.getSubfolders(projectId).pipe(
            timeout(30000),
            catchError(() => of([])),
          );
        }),
        finalize(() => this.loadingProject.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (subs) => {
          this.treeRoot.set(buildFolderTreeFromSubfolders(subs ?? []));
          this.selectedPath.set('');
          this.loadFilesForPath('');
        },
        error: () => {
          this.project.set(null);
          this.treeRoot.set({ label: 'Raíz', path: '', children: [] });
          this.error.set('No se pudo cargar el expediente.');
        },
      });
  }

  private resetTreeAndFiles(): void {
    this.treeRoot.set({ label: 'Raíz', path: '', children: [] });
    this.fileRows.set([]);
    this.selectedPath.set('');
  }

  onFolderSelect(path: string): void {
    this.selectedPath.set(path);
    this.loadFilesForPath(path);
  }

  private toRows(list: IngestRemoteFile[]): IngestFileRow[] {
    return list.map((f) => ({
      ...f,
      sizeLabel: this.formatSize(f),
      typeLabel: this.typeLabel(f),
    }));
  }

  private loadFilesForPath(path: string): void {
    const p = this.project();
    if (!p) {
      this.fileRows.set([]);
      return;
    }
    this.loadingFiles.set(true);
    if (this.ingestEnv.previewMode) {
      this.fileRows.set(this.toRows(getMockRemoteFiles(path)));
      this.loadingFiles.set(false);
      return;
    }
    this.ingestProjectService
      .listFiles(p.id, path)
      .pipe(
        timeout(30000),
        finalize(() => this.loadingFiles.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (list) => {
          this.fileRows.set(this.toRows(list ?? []));
        },
        error: () => {
          this.fileRows.set([]);
        },
      });
  }

  onFileAction(ev: ActionEvent<IngestFileRow>): void {
    if (ev.action !== 'open' || !ev.data.is_folder) return;
    const base = this.selectedPath();
    const name = ev.data.name;
    const next = base ? `${base}/${name}` : name;
    this.onFolderSelect(next);
  }

  formatSize(row: IngestRemoteFile): string {
    if (row.is_folder) return '—';
    if (row.size < 1024) return `${row.size} B`;
    if (row.size < 1024 * 1024) return `${(row.size / 1024).toFixed(1)} KB`;
    return `${(row.size / (1024 * 1024)).toFixed(1)} MB`;
  }

  typeLabel(row: IngestRemoteFile): string {
    return row.is_folder ? 'Carpeta' : 'Archivo';
  }
}
